import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowDownTrayIcon, TrashIcon, FunnelIcon, ListBulletIcon, ShieldExclamationIcon, SparklesIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { DatasetDetail, Row, PaginatedResponse, FilterGroup } from '../types'
import toast from 'react-hot-toast'
import FilterBuilder from '../components/FilterBuilder'
import DataTable from '../components/DataTable'
import ExportModal from '../components/ExportModal'
import SaveListModal from '../components/SaveListModal'
import AddToSuppressionModal from '../components/AddToSuppressionModal'
import NaturalLanguageQuery from '../components/NaturalLanguageQuery'

export default function DatasetDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [page, setPage] = useState(1)
  const [queryMode, setQueryMode] = useState<'filter' | 'nl'>('filter')
  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<FilterGroup | null>(null)
  const [nlQueryResults, setNlQueryResults] = useState<Row[] | null>(null)
  const [nlQuerySql, setNlQuerySql] = useState<string | null>(null)
  const [showExport, setShowExport] = useState(false)
  const [showSaveList, setShowSaveList] = useState(false)
  const [showAddToSuppression, setShowAddToSuppression] = useState(false)
  
  // Fetch dataset details
  const { data: dataset, isLoading: datasetLoading } = useQuery({
    queryKey: ['dataset', id],
    queryFn: async () => {
      const res = await api.get<DatasetDetail>(`/datasets/${id}`)
      return res.data
    },
    enabled: !!id,
  })
  
  // Fetch rows
  const { data: rowsData, isLoading: rowsLoading } = useQuery({
    queryKey: ['dataset-rows', id, page, filter],
    queryFn: async () => {
      if (filter) {
        const res = await api.post<PaginatedResponse<Row>>(
          `/datasets/${id}/rows/filter`,
          { filter },
          { params: { page, page_size: 50 } }
        )
        return res.data
      } else {
        const res = await api.get<{ items: Row[], total: number, page: number, page_size: number, pages: number }>(
          `/datasets/${id}/rows`,
          { params: { page, page_size: 50 } }
        )
        return res.data
      }
    },
    enabled: !!id && !!dataset && queryMode === 'filter',
  })
  
  // Handle NL query results
  const handleNlQueryResults = (results: Row[], sql: string) => {
    setNlQueryResults(results)
    setNlQuerySql(sql)
  }
  
  // Get display data based on query mode
  const displayData = queryMode === 'nl' && nlQueryResults 
    ? { items: nlQueryResults, total: nlQueryResults.length }
    : rowsData
  
  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/datasets/${id}`)
    },
    onSuccess: () => {
      toast.success('Dataset deleted')
      queryClient.invalidateQueries({ queryKey: ['datasets'] })
      navigate('/datasets')
    },
    onError: () => {
      toast.error('Failed to delete dataset')
    },
  })
  
  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this dataset?')) {
      deleteMutation.mutate()
    }
  }
  
  if (datasetLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }
  
  if (!dataset) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Dataset not found</p>
      </div>
    )
  }
  
  const columns = dataset.columns.map(col => col.name)
  
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-start mb-6 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{dataset.name}</h1>
          {dataset.description && (
            <p className="mt-1 text-sm text-gray-600">{dataset.description}</p>
          )}
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span>{dataset.row_count.toLocaleString()} rows</span>
            <span>•</span>
            <span>{dataset.column_count} columns</span>
            <span>•</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              dataset.status === 'ready' 
                ? 'bg-green-100 text-green-700'
                : dataset.status === 'processing'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {dataset.status}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="flex border border-gray-300 rounded-md">
            <button
              onClick={() => { setQueryMode('filter'); setShowFilter(true); }}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                queryMode === 'filter'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-4 w-4 inline mr-1.5" />
              Filter
            </button>
            <button
              onClick={() => { setQueryMode('nl'); setShowFilter(false); }}
              className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                queryMode === 'nl'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SparklesIcon className="h-4 w-4 inline mr-1.5" />
              Natural Language
            </button>
          </div>
          <button
            onClick={() => setShowSaveList(true)}
            className="btn-secondary text-sm px-3 py-1.5"
          >
            <ListBulletIcon className="h-4 w-4 mr-1.5" />
            Save as List
          </button>
          <button
            onClick={() => setShowAddToSuppression(true)}
            className="btn-secondary text-sm px-3 py-1.5"
          >
            <ShieldExclamationIcon className="h-4 w-4 mr-1.5" />
            Add to Suppression
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="btn-primary text-sm px-3 py-1.5"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
            Export
          </button>
          <button
            onClick={handleDelete}
            className="btn-danger text-sm px-3 py-1.5"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Filter panel */}
      {queryMode === 'filter' && showFilter && (
        <div className="card p-4 mb-4 flex-shrink-0">
          <FilterBuilder
            columns={dataset.columns}
            value={filter}
            onChange={setFilter}
          />
        </div>
      )}
      
      {/* Natural Language Query */}
      {queryMode === 'nl' && (
        <div className="card p-4 mb-4 flex-shrink-0">
          <NaturalLanguageQuery
            datasetId={id!}
            onResultsReceived={handleNlQueryResults}
          />
        </div>
      )}
      
      {/* Data table */}
      <div className="card flex-1 flex flex-col min-h-0">
        {(rowsLoading && queryMode === 'filter') ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : displayData?.items.length ? (
          <>
            <div className="flex-1 min-h-0">
              <DataTable
                columns={columns}
                rows={displayData.items}
                maxHeight="100%"
              />
            </div>
            {/* Pagination - only show for filter mode */}
            {queryMode === 'filter' && rowsData && (
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 flex-shrink-0">
              <div className="text-sm text-gray-700">
                Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, rowsData.total)} of{' '}
                {rowsData.total.toLocaleString()} rows
              </div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary text-sm px-3 py-1"
                >
                  Previous
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Page</span>
                  <input
                    type="number"
                    min="1"
                    max={rowsData.pages}
                    value={page}
                    onChange={(e) => {
                      const val = parseInt(e.target.value)
                      if (val >= 1 && val <= rowsData.pages) {
                        setPage(val)
                      }
                    }}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value)
                      if (isNaN(val) || val < 1) {
                        setPage(1)
                      } else if (val > rowsData.pages) {
                        setPage(rowsData.pages)
                      }
                    }}
                    className="w-16 px-2 py-1 text-sm text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <span className="text-sm text-gray-600">of {rowsData.pages}</span>
                </div>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= rowsData.pages}
                  className="btn-secondary text-sm px-3 py-1"
                >
                  Next
                </button>
              </div>
            </div>
            )}
            
            {/* NL Query Result Info */}
            {queryMode === 'nl' && nlQueryResults && (
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 flex-shrink-0">
                <div className="text-sm text-gray-700">
                  Showing {nlQueryResults.length} results from natural language query
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No rows found</p>
          </div>
        )}
      </div>
      
      {/* Export modal */}
      {showExport && (
        <ExportModal
          dataset={dataset}
          filter={queryMode === 'filter' ? filter : null}
          sqlQuery={queryMode === 'nl' ? nlQuerySql : null}
          onClose={() => setShowExport(false)}
        />
      )}
      
      {/* Save as List modal */}
      {showSaveList && (
        <SaveListModal
          datasetId={dataset.id}
          filter={filter}
          rowCount={rowsData?.total || 0}
          onClose={() => setShowSaveList(false)}
        />
      )}
      
      {/* Add to Suppression List modal */}
      {showAddToSuppression && (
        <AddToSuppressionModal
          datasetId={dataset.id}
          filter={filter}
          rowCount={rowsData?.total || 0}
          onClose={() => setShowAddToSuppression(false)}
        />
      )}
    </div>
  )
}
